import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import * as cronstrue from 'cronstrue';
import { PermissionsService } from '../../common/services/permissions.service';
import { Schedule } from '../schedule.models';
import { NotifyService } from '../../common/services/notify.service';
import { ConfirmationDialogService } from '../../common/services/confirmation-dialog.service';
import { AppState } from '../../store';
import { ItemType } from '../../common/components/lists/list-item-snippet.component';
import { duplicateSchedule, removeSchedule, runSchedule } from '../store/schedules.actions';
import { DialogType } from '../../common/services/components/dialog-template.component';

@Component({
  selector: 'schedule-list-item',
  template: `
    <div class="generic-list-item slider-animation" [ngClass]="{ 'closed-animation': item.isRemoved }">
      <div
        class="schedule-list-item"
        [ngClass]="{
          selected: item.selected,
          'pending-removal': item.pending_removal,
          inactive: item.status === 'disabled'
        }"
        id="schedule-{{ item.id }}"
      >
        <div class="schedule-name">
          <svg class="icon icons-master-expanded_icon-navmain-schedules">
            <use href="#img-sprite_icons-master-expanded_icon-navmain-schedules"></use>
          </svg>
          <div>
            <strong
              ><a
                (click)="editSchedule(item)"
                [matTooltip]="item.name"
                matTooltipPosition="right"
                matTooltipClass="right wide"
                >{{ item.name | xpLengthCheck: 40 }}</a
              >
              <span *ngIf="item.status === 'disabled'" class="inactive"
                >({{ 'schedule.generic-list.item.labels.inactive' | translate }})</span
              ></strong
            >
            <p class="schedule-description" *ngIf="item.description">{{ item.description | xpLengthCheck: 80 }}</p>
          </div>
        </div>
        <div class="schedule-owner">
          <div>
            <a (click)="goToOwner(item)">{{ item.owner.name || item.owner.email | xpLengthCheck: 20 }}</a>
          </div>
        </div>
        <div class="schedule-repeat">
          <strong *ngIf="item.packages_count != null">{{
            'schedule.generic-list.item.labels.packages_count' | translate: { count: item.packages_count }
          }}</strong>
          <span *ngIf="item.schedule_type === 'cron'">{{ scheduleMessage }}</span>
          <span *ngIf="item.schedule_type === 'interval'">{{
            'schedule.generic-list.item.labels.intervals.' + item.interval_unit
              | translate: { interval_amount: item.interval_amount }
          }}</span>
        </div>
        <div class="schedule-last-run">
          <span *ngIf="item.last_run_at == null">{{ 'schedule.generic-list.item.labels.never' | translate }}</span>
          <span
            *ngIf="item.last_run_at != null"
            [matTooltip]="item.last_run_at | xpDateUTC"
            matTooltipPosition="above"
            >{{ item.last_run_at | xpDateWithTime }}</span
          >
        </div>
        <div class="schedule-next-run">
          <span
            *ngIf="item.next_run_at != null"
            [matTooltip]="item.next_run_at | xpDateUTC"
            matTooltipPosition="above"
            >{{ item.next_run_at | xpDateWithTime }}</span
          >
        </div>
        <div class="dropdown xp-dropdown-full" [matMenuTriggerFor]="dropdown">
          <i class="fa fa-ellipsis-v"></i>
        </div>
        <mat-menu #dropdown="matMenu" class="full-dropdown-menu">
          <li mat-menu-item (click)="runSchedule(item)" *ngxPermissionsOnly="'createJob'">
            <span>{{ 'schedule.generic-list.item.actions.run' | translate }}</span>
          </li>
          <li mat-menu-item (click)="editSchedule(item)" class="top-separator">
            <span>{{ 'schedule.generic-list.item.actions.edit' | translate }}</span>
          </li>
          <li mat-menu-item (click)="duplicateSchedule(item)" *ngxPermissionsOnly="'createSchedule'">
            <span>{{ 'schedule.generic-list.item.actions.duplicate' | translate }}</span>
          </li>
          <li mat-menu-item (click)="onRemove(item)" *ngxPermissionsOnly="'deleteSchedule'" class="top-separator">
            <span class="text-danger">{{ 'schedule.generic-list.item.actions.delete' | translate }}</span>
          </li>
        </mat-menu>
      </div>
    </div>
  `,
})
export class ScheduleListItemComponent {
  @Input() item: Schedule;
  @Output() edit = new EventEmitter<Schedule>();

  constructor(
    private permissionsService: PermissionsService,
    private translate: TranslateService,
    private notify: NotifyService,
    private confirmationDialog: ConfirmationDialogService,
    private store: Store<AppState>,
    @Inject('$rootScope') private $rootScope: any,
  ) {}

  editSchedule(item: Schedule) {
    this.edit.emit(item);
  }

  duplicateSchedule(item: Schedule) {
    this.store.dispatch(duplicateSchedule({ schedule: item }));
  }

  hasPermission(permissionName) {
    return this.permissionsService.hasPermission(permissionName);
  }

  goToOwner(item: Schedule): void {
    this.$rootScope.stateGo('app.account.settings.account.member', item.owner);
  }

  runSchedule(item: Schedule): void {
    const dialogRef = this.confirmationDialog.openDialog({
      title: this.translate.instant('schedule.generic-list.item.modals.run.confirm.title'),
      hint: this.translate.instant('schedule.generic-list.item.modals.run.confirm.hint'),
      yes: this.translate.instant('schedule.generic-list.item.modals.run.confirm.yes'),
      no: this.translate.instant('schedule.generic-list.item.modals.run.confirm.no'),
      item,
      itemType: ItemType.schedule,
      type: DialogType.warning,
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.store.dispatch(runSchedule({ schedule: item }));
      }
    });
  }

  get scheduleMessage(): string {
    return cronstrue.toString(this.item.schedule_expression.toUpperCase());
  }

  onRemove(item: Schedule): void {
    const dialogRef = this.confirmationDialog.openDialog({
      title: this.translate.instant('schedule.generic-list.item.modals.confirm.title'),
      hint: this.translate.instant('schedule.generic-list.item.modals.confirm.hint'),
      yes: this.translate.instant('schedule.generic-list.item.modals.confirm.yes'),
      no: this.translate.instant('schedule.generic-list.item.modals.confirm.no'),
      item,
      itemType: ItemType.schedule,
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.store.dispatch(removeSchedule({ scheduleId: item.id }));
      }
    });
  }
}
